import Link from 'next/link';
import {useRef} from 'react';

import CSS from './Tabs.module.scss';
import Focus from './Focus';
import Slider, {type SliderHandlers} from '../Slider';
import Vector, {type VectorName} from './vector/Vector';
import {WindowSizeThresholds} from '@/hooks/window-size';

interface TabConfig {
  title: string;
  vectorName?: VectorName;
  isActive?: boolean;
  isDisabled?: boolean;
  className?: HTMLElement['className'];
  /** The destination link for the tab. */
  href?: HTMLAnchorElement['href'];
  /** An arbitrary data attribute. */
  dataAttribute?: string | number;
  callback: () => void;
}

function TabChildren({tab}: {tab: TabConfig}) {
  return (
    <>
      {!!tab.vectorName && (
        <Vector
          name={tab.vectorName}
          className={CSS.icon}
          svgProps={{size: '1rem'}}
        />
      )}
      <span className={[CSS.title, tab.isActive ? CSS.expand : ''].join(' ')}>
        {tab.title}
      </span>
    </>
  );
}

function Tab({
  tab,
  hasLimitedSpace,
  sliderHandlers
}: {
  tab: TabConfig;
  sliderHandlers: React.RefObject<SliderHandlers | null>;
  hasLimitedSpace?: boolean;
}) {
  const attributes = {
    ...sliderHandlers.current,
    'data-attribute': tab.dataAttribute || '',
    onClick: tab.isDisabled
      ? undefined
      : (event: React.MouseEvent) =>
          sliderHandlers.current!.onClick(event, () => tab.callback()),
    className: [
      CSS.tab,
      tab.className || '',
      tab.isActive ? CSS.active : '',
      tab.isDisabled ? CSS.disabled : '',
      hasLimitedSpace ? CSS.limitedSpace : ''
    ].join(' ')
  };

  return (
    <Focus tabIndex={tab.isDisabled ? -1 : undefined}>
      {tab.href ? (
        <Link href={tab.href} {...attributes}>
          <TabChildren tab={tab} />
        </Link>
      ) : (
        <div {...attributes}>
          <TabChildren tab={tab} />
        </div>
      )}
    </Focus>
  );
}

export default function Tabs({
  tabs,
  className,
  wrapperClassName,
  shortHeightThreshold,
  hasShortHeight,
  limitedSpaceThreshold,
  hasLimitedSpace,
  showSliderControls,
  hasFullWidth
}: {
  tabs: TabConfig[];
  className?: HTMLElement['className'];
  wrapperClassName?: HTMLElement['className'];
  /**
   * Specifying a value, implicitly marks the {@link hasShortHeight}
   * property as `true`.
   */
  shortHeightThreshold?: WindowSizeThresholds;
  hasShortHeight?: boolean;
  /**
   * Specifying a value, implicitly marks the {@link hasLimitedSpace}
   * property as `true`.
   */
  limitedSpaceThreshold?: WindowSizeThresholds;
  /**
   * Only shows the tab's title when tab is active.
   * The tab's icons are always visible.
   */
  hasLimitedSpace?: boolean;
  showSliderControls?: boolean;
  hasFullWidth?: boolean;
}) {
  const sliderHandlers = useRef<SliderHandlers | null>(null);

  return (
    <Slider
      ref={sliderHandlers}
      data-short-height-threshold={shortHeightThreshold}
      data-limited-space-threshold={limitedSpaceThreshold}
      showControls={showSliderControls}
      wrapperClassName={[
        hasFullWidth ? CSS.wrapperFullWidth : '',
        wrapperClassName || ''
      ].join(' ')}
      className={[
        CSS.tabs,
        className || '',
        hasShortHeight ? CSS.shortHeight : '',
        hasLimitedSpace ? CSS.limitedSpace : ''
      ].join(' ')}>
      {tabs.map((tab, index) => (
        <Tab
          key={index}
          tab={tab}
          hasLimitedSpace={hasLimitedSpace}
          sliderHandlers={sliderHandlers}
        />
      ))}
    </Slider>
  );
}
